/**
 * Utility for detecting and normalizing referral sources
 */

export interface ReferralData {
  isPaidReferrer: boolean;
  referralSource: string;
  referralUrl?: string; // Full referrer URL
  landingPage?: string; // The page they first landed on
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
}

/**
 * Detect if traffic is from paid sources based on UTM parameters and tracking IDs
 */
export function detectReferralSource(searchParams: URLSearchParams, referrer?: string, currentUrl?: string): ReferralData {
  // Extract UTM parameters
  const utmSource = searchParams.get('utm_source') || undefined;
  const utmMedium = searchParams.get('utm_medium') || undefined;
  const utmCampaign = searchParams.get('utm_campaign') || undefined;
  const utmContent = searchParams.get('utm_content') || undefined;
  const utmTerm = searchParams.get('utm_term') || undefined;

  // Extract tracking IDs
  const fbclid = searchParams.get('fbclid') || undefined; // Facebook Click ID
  const gclid = searchParams.get('gclid') || undefined;   // Google Click ID
  const ttclid = searchParams.get('ttclid') || undefined; // TikTok Click ID

  // Determine if it's paid traffic
  let isPaidReferrer = false;
  let referralSource = 'direct'; // Default to direct instead of organic

  // Check for Meta/Facebook Ads
  if (fbclid ||
      utmSource?.toLowerCase().includes('facebook') ||
      utmSource?.toLowerCase().includes('fb') ||
      utmSource?.toLowerCase().includes('instagram') ||
      utmSource?.toLowerCase().includes('ig') ||
      utmSource?.toLowerCase().includes('meta')) {
    isPaidReferrer = true;
    referralSource = 'facebook'; // Simplified - just 'facebook' for all Meta ads
  }
  // Check for Google Ads
  else if (gclid ||
           (utmSource?.toLowerCase().includes('google') && utmMedium === 'cpc') ||
           utmSource === 'adwords') {
    isPaidReferrer = true;
    referralSource = 'google'; // Simplified - just 'google' for all Google ads
  }
  // Check for TikTok Ads
  else if (ttclid || utmSource?.toLowerCase().includes('tiktok')) {
    isPaidReferrer = true;
    referralSource = 'tiktok'; // Simplified - just 'tiktok'
  }
  // Check for other paid media
  else if (utmMedium === 'cpc' ||
           utmMedium === 'ppc' ||
           utmMedium === 'cpm' ||
           utmMedium === 'paid' ||
           utmMedium === 'paidsocial' ||
           utmMedium === 'paid_social') {
    isPaidReferrer = true;
    referralSource = normalizeReferralSource(utmSource || 'paid_unknown');
  }
  // Check for email campaigns
  else if (utmMedium === 'email' || utmSource?.toLowerCase().includes('email')) {
    referralSource = 'email';
  }
  // Check for social (organic)
  else if (utmMedium === 'social' ||
           referrer?.includes('facebook.com') ||
           referrer?.includes('instagram.com') ||
           referrer?.includes('twitter.com') ||
           referrer?.includes('linkedin.com') ||
           referrer?.includes('reddit.com')) {
    if (referrer?.includes('facebook.com')) referralSource = 'facebook';
    else if (referrer?.includes('instagram.com')) referralSource = 'instagram';
    else if (referrer?.includes('twitter.com')) referralSource = 'twitter';
    else if (referrer?.includes('linkedin.com')) referralSource = 'linkedin';
    else if (referrer?.includes('reddit.com')) referralSource = 'reddit';
    else referralSource = 'social';
  }
  // Check for partner referrals
  else if (searchParams.get('partner') || searchParams.get('ref')) {
    referralSource = searchParams.get('partner') || searchParams.get('ref') || 'partner';
  }
  // Check for direct/typed traffic
  else if (!referrer && !utmSource) {
    referralSource = 'direct';
  }
  // Check for search engines (organic)
  else if (referrer?.includes('google.') ||
           referrer?.includes('bing.') ||
           referrer?.includes('yahoo.') ||
           referrer?.includes('duckduckgo.')) {
    if (referrer?.includes('google.')) referralSource = 'google';
    else if (referrer?.includes('bing.')) referralSource = 'bing';
    else if (referrer?.includes('yahoo.')) referralSource = 'yahoo';
    else if (referrer?.includes('duckduckgo.')) referralSource = 'duckduckgo';
    else referralSource = 'search';
  }
  // Use UTM source if available
  else if (utmSource) {
    referralSource = normalizeReferralSource(utmSource);
  }
  // Use referrer domain if available
  else if (referrer) {
    try {
      const url = new URL(referrer);
      const hostname = url.hostname.replace('www.', '');

      // Simplify to just the main platform
      if (hostname.includes('facebook.com')) {
        referralSource = 'facebook';
      } else if (hostname.includes('instagram.com')) {
        referralSource = 'instagram';
      } else if (hostname.includes('linkedin.com')) {
        referralSource = 'linkedin';
      } else if (hostname.includes('google.')) {
        referralSource = 'google';
      } else if (hostname.includes('twitter.com') || hostname.includes('t.co')) {
        referralSource = 'twitter';
      } else if (hostname.includes('reddit.com')) {
        referralSource = 'reddit';
      } else if (hostname.includes('youtube.com')) {
        referralSource = 'youtube';
      } else if (hostname.includes('tiktok.com')) {
        referralSource = 'tiktok';
      } else {
        // For other sites, just use the domain name
        referralSource = hostname.split('.')[0] || 'unknown'; // Get just the main domain
      }
    } catch {
      referralSource = 'unknown';
    }
  }

  return {
    isPaidReferrer,
    referralSource,
    referralUrl: referrer || undefined,
    landingPage: currentUrl || undefined,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    fbclid,
    gclid,
    ttclid
  };
}

/**
 * Normalize referral source to consistent format
 */
function normalizeReferralSource(source: string): string {
  const normalized = source.toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  // Map common variations to standard names
  const mappings: Record<string, string> = {
    'fb': 'facebook',
    'ig': 'instagram',
    'yt': 'youtube',
    'tw': 'twitter',
    'li': 'linkedin',
    'reddit': 'reddit',
    'tiktok': 'tiktok',
    'snapchat': 'snapchat',
    'pinterest': 'pinterest'
  };

  return mappings[normalized] || normalized;
}

/**
 * Store referral data in session storage for persistence across navigation
 */
export function storeReferralData(data: ReferralData) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('alli_referral_data', JSON.stringify(data));
  }
}

/**
 * Retrieve stored referral data from session storage
 */
export function getStoredReferralData(): ReferralData | null {
  if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem('alli_referral_data');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Get or detect referral data (checks storage first, then detects from current page)
 */
export function getReferralData(): ReferralData {
  // Check stored data first (in case user navigated around)
  const stored = getStoredReferralData();
  if (stored) {
    return stored;
  }

  // Detect from current page
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;
    const data = detectReferralSource(searchParams, referrer);

    // Store for future use
    storeReferralData(data);

    return data;
  }

  // Default fallback
  return {
    isPaidReferrer: false,
    referralSource: 'unknown'
  };
}